import type {
  HealthReport,
  PublicConfig,
  PublicQuiz,
  QuizAttemptResult,
  QuizCounts,
  QuizLevel,
  VideoHistoryPage,
  VideoMode,
  VideoSummary,
} from "@lib/domain";
import type { DiagnosticsLog } from "@lib/diagnostics";
import { ApiError, type ApiErrorBody } from "@/lib/errors";
import {
  mockCreateTextVideo,
  mockCreateVideo,
  mockGetConfig,
  mockGetDiagnostics,
  mockGetHealth,
  mockGetHistory,
  mockGetQuiz,
  mockGetVideo,
  mockSubmitAttempt,
  mockSubmitTranscript,
  mockUpdateQuizOptions,
} from "@/lib/mockApi";

export type { ApiErrorBody } from "@/lib/errors";
export { ApiError } from "@/lib/errors";

const API_BASE = "/api/v1";

const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === "1";

interface Envelope<T> {
  data: T;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", ...init?.headers },
    });
  } catch {
    throw new ApiError(0, { code: "NETWORK_ERROR", message: "Could not reach the server." });
  }

  let body: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, {
        code: "BAD_RESPONSE",
        message: "The server returned an invalid response.",
      });
    }
  }

  if (!response.ok) {
    const err = (body as { error?: ApiErrorBody } | null)?.error;
    throw new ApiError(
      response.status,
      err ?? { code: "HTTP_ERROR", message: `Request failed (${response.status}).` },
    );
  }

  return (body as Envelope<T>).data;
}

export function createVideo(
  url: string,
  mode: VideoMode,
  questionCounts?: QuizCounts,
): Promise<VideoSummary> {
  if (USE_MOCKS) return mockCreateVideo(url, mode, questionCounts);
  return request<VideoSummary>("/videos", {
    method: "POST",
    body: JSON.stringify({ url, mode, ...(questionCounts ? { questionCounts } : {}) }),
  });
}

export function updateQuizOptions(
  videoId: string,
  questionCounts: QuizCounts,
  paid?: boolean,
): Promise<QuizCounts> {
  if (USE_MOCKS) return mockUpdateQuizOptions(videoId, questionCounts, paid);
  return request<QuizCounts>(`/videos/${encodeURIComponent(videoId)}`, {
    method: "PATCH",
    body: JSON.stringify(questionCounts),
    headers: paid ? { "x-vidquiz-payment": "paid" } : {},
  });
}

/** Build a quiz from pasted/typed source text (no YouTube video). */
export function createTextVideo(
  text: string,
  questionCounts?: QuizCounts,
  paid?: boolean,
): Promise<VideoSummary> {
  if (USE_MOCKS) return mockCreateTextVideo(text, questionCounts, paid);
  return request<VideoSummary>("/videos/text", {
    method: "POST",
    body: JSON.stringify({ text, ...(questionCounts ? { questionCounts } : {}) }),
    headers: paid ? { "x-vidquiz-payment": "paid" } : {},
  });
}

/** A transcript the user's browser gathered and posted back for a video. */
export interface TranscriptSubmission {
  segments?: Array<{ start: number; duration: number; text: string }>;
  text?: string;
  language?: string | null;
  source?: "browser" | "paste";
}

/** Supply a transcript for a video whose server-side fetch was blocked. */
export function submitTranscript(
  videoId: string,
  submission: TranscriptSubmission,
): Promise<VideoSummary> {
  if (USE_MOCKS) return mockSubmitTranscript(videoId, submission);
  return request<VideoSummary>(`/videos/${encodeURIComponent(videoId)}/transcript`, {
    method: "POST",
    body: JSON.stringify(submission),
  });
}

export function getVideo(videoId: string): Promise<VideoSummary> {
  if (USE_MOCKS) return mockGetVideo(videoId);
  return request<VideoSummary>(`/videos/${encodeURIComponent(videoId)}`);
}

export function getQuiz(videoId: string, level: QuizLevel = 1): Promise<PublicQuiz> {
  if (USE_MOCKS) return mockGetQuiz(videoId, level);
  return request<PublicQuiz>(`/videos/${encodeURIComponent(videoId)}/quiz?level=${level}`);
}

export function submitAttempt(videoId: string, answers: number[], level: QuizLevel = 1): Promise<QuizAttemptResult> {
  if (USE_MOCKS) return mockSubmitAttempt(videoId, answers, level);
  return request<QuizAttemptResult>(`/videos/${encodeURIComponent(videoId)}/quiz/attempts`, {
    method: "POST",
    body: JSON.stringify({ answers, level }),
  });
}

export function getConfig(): Promise<PublicConfig> {
  if (USE_MOCKS) return mockGetConfig();
  return request<PublicConfig>("/config");
}

export function getHealth(): Promise<HealthReport> {
  if (USE_MOCKS) return mockGetHealth();
  return request<HealthReport>("/health");
}

export function getDiagnostics(videoId: string): Promise<DiagnosticsLog> {
  if (USE_MOCKS) return mockGetDiagnostics();
  return request<DiagnosticsLog>(`/videos/${encodeURIComponent(videoId)}/diagnostics`);
}

export interface HistoryQuery {
  search?: string;
  limit?: number;
  cursor?: string;
}

export function getHistory(query: HistoryQuery = {}): Promise<VideoHistoryPage> {
  if (USE_MOCKS) return mockGetHistory(query);
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor) params.set("cursor", query.cursor);
  const qs = params.toString();
  return request<VideoHistoryPage>(`/videos/history${qs ? `?${qs}` : ""}`);
}